/* global console */
import { createSlice, current } from "@reduxjs/toolkit";
import { findCol, findRow, getUnique, unique } from "../helpers/array";

const selectionSlice = createSlice({
  name: "selection",
  initialState: {
    table: false,
    cells: [],
    columns: [],
    rows: [],
    borders: [],
    slot: {},
    hover: {
      cell: null,
    },
    lastSelectionStart: null,
  },
  reducers: {
    extendShiftSelection: (state, action) => {
      const { start, end, cells, mergedCells } = action.payload;

      if (!start || !end || !cells || !mergedCells) {
        console.error("Invalid shift selection input", { start, end, cells, mergedCells });
        return;
      }

      // Reset or initialize selection
      if (!state.lastSelectionStart) {
        state.cells = [];
        state.lastSelectionStart = start;
      }

      // Use the same box selection logic as in the select reducer
      const first = state.cells[0] || start;
      state.cells.splice(1, state.cells.length);

      const corner = end;

      if (corner.x >= first.x && corner.y >= first.y) {
        // toward right-down
        for (let x = first.x; x <= corner.x; x++) {
          for (let y = first.y; y <= corner.y; y++) {
            const c = cells[x][y];
            state.cells.push({ id: c.id, x, y });
          }
        }
      } else if (corner.x >= first.x && corner.y <= first.y) {
        // toward left-down
        for (let x = first.x; x <= corner.x; x++) {
          for (let y = first.y; y >= corner.y; y--) {
            const c = cells[x][y];
            state.cells.push({ id: c.id, x, y });
          }
        }
      } else if (corner.x <= first.x && corner.y >= first.y) {
        // toward right-up
        for (let x = first.x; x >= corner.x; x--) {
          for (let y = first.y; y <= corner.y; y++) {
            const c = cells[x][y];
            state.cells.push({ id: c.id, x, y });
          }
        }
      } else {
        // toward left-up
        for (let x = first.x; x >= corner.x; x--) {
          for (let y = first.y; y >= corner.y; y--) {
            const c = cells[x][y];
            state.cells.push({ id: c.id, x, y });
          }
        }
      }

      state.lastSelectionStart = start;
    },
    selectWholeRowColumn: (state, action) => {
      const { isRow, isWhole, index, colCount, rowCount } = action.payload;
      // Reset selection
      state.table = false;
      state.cells = [];
      state.columns = [];
      state.rows = [];
      state.borders = [];
      state.slot = null;

      if (isRow) {
        // Select whole row or specific row
        if (isWhole) {
          // Select all rows
          state.rows = Array.from({ length: rowCount }, (_, i) => i);
          state.table = true;
        } else {
          state.rows.push(index);
        }
      } else {
        // Select whole column or specific column
        if (isWhole) {
          // Select all columns
          state.columns = Array.from({ length: colCount }, (_, i) => i);
          state.table = true;
        } else {
          state.columns.push(index);
          // Update hover cell with spacer information
        }
      }
    },

    reset: (state) => {
      state.table = false;
      state.cells = [];
      state.columns = [];
      state.rows = [];
      state.borders = [];
      state.slot = null;
      state.hover.cell = null;
      state.cellBeingEdited = null;
      state.lastSelectionStart = null;
    },
    select: (state, action) => {
      if (action.payload.cell) {
        // First, check if we're adding to an existing row or column selection
        if (
          (action.payload.type === "additive" || action.payload.type === "box") &&
          (state.rows.length > 0 || state.columns.length > 0)
        ) {
          // Keep the existing row/column selections and add the cell
          state.cells.push(action.payload.cell);
          return;
        }

        // do nothing if cell already part of a larger selection
        if (
          state.columns.indexOf((c) => c === action.payload.cell.y) !== -1 ||
          state.rows.indexOf((c) => c === action.payload.cell.x) !== -1
        ) {
          return;
        }

        state.table = false;
        state.borders = [];
        state.columns = [];
        state.rows = [];
        state.slot = null;
        if (action.payload.type === "clear") {
          state.cells.splice(0, state.cells.length);
          state.lastSelectionStart = null;
        }

        state.cells.push(action.payload.cell);

        if (action.payload.type === "box") {
          const first = state.cells[0];
          state.cells.splice(1, state.cells.length);
          const corner = action.payload.cell;
          if (corner.x >= first.x && corner.y >= first.y) {
            // toward right-down
            for (let x = first.x; x <= corner.x; x++) {
              for (let y = first.y; y <= corner.y; y++) {
                const c = action.payload.cells[x][y];
                state.cells.push({ id: c.id, x, y });
              }
            }
          } else if (corner.x >= first.x && corner.y <= first.y) {
            // toward left-down
            for (let x = first.x; x <= corner.x; x++) {
              for (let y = first.y; y >= corner.y; y--) {
                const c = action.payload.cells[x][y];
                state.cells.push({ id: c.id, x, y });
              }
            }
          } else if (corner.x <= first.x && corner.y >= first.y) {
            for (let x = first.x; x >= corner.x; x--) {
              for (let y = first.y; y <= corner.y; y++) {
                const c = action.payload.cells[x][y];
                state.cells.push({ id: c.id, x, y });
              }
            }
          } else {
            for (let x = first.x; x >= corner.x; x--) {
              for (let y = first.y; y >= corner.y; y--) {
                const c = action.payload.cells[x][y];
                state.cells.push({ id: c.id, x, y });
              }
            }
          }
        }
      }

      if (action.payload.column !== undefined) {
        state.table = false;
        state.borders = [];
        state.cells = [];
        state.rows = [];
        state.slot = null;
        if (action.payload.type === "clear") {
          state.columns.splice(0, state.columns.length);
        }

        state.columns.push(action.payload.column);

        // Update hover cell with spacer information

        if (action.payload.type === "box") {
          const first = state.columns[0];
          if (action.payload.column > first) {
            for (let i = first; i <= action.payload.column; i++) {
              state.columns.push(i);
            }
          } else {
            for (let i = first; i >= action.payload.column; i--) {
              state.columns.push(i);
            }
          }
        }
      }

      if (action.payload.row !== undefined) {
        state.table = false;
        state.borders = [];
        state.cells = [];
        state.columns = [];
        state.slot = null;
        if (action.payload.type === "clear") {
          state.rows.splice(0, state.rows.length);
        }

        state.rows.push(action.payload.row);

        // Update hover cell with spacer information

        if (action.payload.type === "box") {
          const first = state.rows[0];
          if (action.payload.row > first) {
            for (let i = first; i <= action.payload.row; i++) {
              state.rows.push(i);
            }
          } else {
            for (let i = first; i >= action.payload.row; i--) {
              state.rows.push(i);
            }
          }
        }
      }

      if (action.payload.border !== undefined) {
        state.table = false;
        state.cells = [];
        state.columns = [];
        state.rows = [];
        state.slot = null;
        state.borders.shift();
        state.borders.push(action.payload.border);
      }

      if (action.payload.slot !== undefined) {
        state.table = false;
        state.cells = [];
        state.columns = [];
        state.rows = [];
        state.borders = [];
        state.slot = { vertical: action.payload.slot.vertical, index: action.payload.slot.index };
      }

      if (state.cells.length > 1) {
        const uniqueCells = getUnique(state.cells, "id");
        state.cells = uniqueCells;
      }

      if (action.payload.table) {
        state.table = true;
      }
    },
    deselect: (state, action) => {
      if (action.payload.cell) {
        const index = state.cells.findIndex((c) => c.id === action.payload.cell.id);
        if (index !== -1) state.cells.splice(index, 1);
        state.table = false;
      }

      if (action.payload.column !== undefined) {
        const index = state.columns.findIndex((c) => c === action.payload.column);
        if (index !== -1) state.columns.splice(index, 1);
        state.table = false;
      }

      if (action.payload.row !== undefined) {
        const index = state.rows.findIndex((c) => c === action.payload.row);
        if (index !== -1) state.rows.splice(index, 1);
        state.table = false;
      }

      if (action.payload.border !== undefined) {
        const index = state.borders.findIndex((c) => c === action.payload.border);
        if (index !== -1) state.borders.splice(index, 1);
        state.table = false;
      }
    },
    toggle: (state, action) => {
      if (action.payload.cell) {
        const index = state.cells.findIndex((c) => c.id === action.payload.cell.id);
        if (index !== -1) state.cells.splice(index, 1);
        else state.cells.push(action.payload.cell);
        state.table = false;
      }

      if (action.payload.column !== undefined) {
        const index = state.columns.findIndex((c) => c === action.payload.column);
        if (index !== -1) state.columns.splice(index, 1);
        else state.columns.push(action.payload.column);
        state.table = false;
      }

      if (action.payload.row !== undefined) {
        const index = state.rows.findIndex((c) => c === action.payload.row);
        if (index !== -1) state.rows.splice(index, 1);
        else state.rows.push(action.payload.row);
        state.table = false;
      }
    },
    normalize: (state, action) => {
      // If this is an additive selection (ctrl key), don't convert cells to rows/columns
      if (action.payload.isAdditive) {
        if (
          state.cells.length === action.payload.rowCount * action.payload.colCount ||
          state.columns.length === action.payload.colCount ||
          state.rows.length === action.payload.rowCount
        ) {
          state.table = true;
          return;
        }
        return;
      }
      if (state.table && action.payload.cells) {
        state.cells.splice(0);
        for (let i = 0; i < action.payload.rowCount; i++) {
          for (let j = 0; j < action.payload.colCount; j++) {
            const c = action.payload.cells[i][j];
            state.cells.push({ id: c.id, x: i, y: j });
          }
        }
        console.log("normalized", { action, state: current(state) });
        return;
      }

      if (action.payload.rowCount && action.payload.colCount) {
        for (let i = 0; i < action.payload.colCount; i++) {
          const colFoundInSelection = findCol(state.cells, i, action.payload.rowCount);
          if (colFoundInSelection && !state.columns.includes(i)) {
            state.columns.push(i);
          }
        }
        if (state.columns.length > 0) {
          state.cells.splice(0);
          state.columns = unique(state.columns);

          if (state.columns.length === action.payload.colCount) {
            state.table = true;
          }
          return;
        }

        // find fully selected rows, remove cells from state.cells and add row index to state.rows
        for (let i = 0; i < action.payload.rowCount; i++) {
          const rowFoundInSelection = findRow(state.cells, i, action.payload.colCount);
          if (rowFoundInSelection && !state.rows.includes(i)) {
            state.rows.push(i);
          }
        }
        if (state.rows.length > 0) {
          state.cells.splice(0);
          state.rows = unique(state.rows);

          if (state.rows.length === action.payload.rowCount) {
            state.table = true;
          }
          return;
        }
      }

      console.log("Normalized selection", { payload: action.payload, state: current(state) });
    },
    hover: (state, action) => {
      state.hover.cell = action.payload.cell;
    },
  },
});

export const { reset, select, deselect, toggle, normalize, hover, extendShiftSelection, selectWholeRowColumn } =
  selectionSlice.actions;

export default selectionSlice.reducer;
